import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, UserPlus, Phone, AlertTriangle } from 'lucide-react';
import { Contact } from '../../../types/models/whatsapp';

interface NewChatDialogProps {
  open: boolean;
  contacts: Contact[];
  onClose: () => void;
  /** Resolves to the conversation id to open. */
  onStart: (phone: string, name?: string) => Promise<string>;
  onOpenConversation: (conversationId: string) => void;
}

/** Digits only, with the leading + stripped — matches the server's key format. */
const looksLikePhone = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
};

const formatPhone = (phone: string): string => `+${phone}`;

/**
 * Starts a conversation, either with an existing contact or a new number.
 *
 * A brand-new conversation is always outside the 24h window, so the composer
 * will require a template — that is stated here rather than discovered after
 * the thread opens.
 */
const NewChatDialog: React.FC<NewChatDialogProps> = ({
  open,
  contacts,
  onClose,
  onStart,
  onOpenConversation
}) => {
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset when reopened, so a previous attempt never leaks into the next.
  useEffect(() => {
    if (open) {
      setSearch('');
      setName('');
      setError(null);
    }
  }, [open]);

  const term = search.trim().toLowerCase();
  const digits = term.replace(/\D/g, '');

  const matches = useMemo(() => {
    if (!term) return contacts.slice(0, 50);
    return contacts
      .filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          (digits.length > 0 && c.phone.includes(digits))
      )
      .slice(0, 50);
  }, [contacts, term, digits]);

  // Offer "start a new number" only when the typed value is a plausible phone
  // that isn't already a contact.
  const isNewNumber =
    looksLikePhone(search) && !contacts.some((c) => c.phone.includes(digits));

  if (!open) return null;

  const start = async (phone: string, contactName?: string) => {
    if (starting) return;
    setStarting(true);
    setError(null);
    try {
      const conversationId = await onStart(phone, contactName);
      onOpenConversation(conversationId);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Could not start this conversation');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-20">
      <div className="flex max-h-[70vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            New chat
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-gray-200 p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contacts or type a phone number"
              className="w-full rounded-lg border-gray-300 pl-9 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {isNewNumber && (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              className="mt-2 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          )}

          {error && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-danger-600">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {error}
            </p>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isNewNumber && (
            <button
              type="button"
              disabled={starting}
              onClick={() => start(search.replace(/\D/g, ''), name || undefined)}
              className="flex w-full items-center gap-3 border-b border-gray-100 p-3 text-left hover:bg-gray-50 disabled:opacity-50"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-wa-green text-white">
                <UserPlus className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">
                  Start chat with {formatPhone(search.replace(/\D/g, ''))}
                </span>
                <span className="block text-xs text-gray-500">
                  New contact — a template is required to open the conversation
                </span>
              </span>
            </button>
          )}

          {matches.map((contact) => (
            <button
              key={contact.id}
              type="button"
              disabled={starting || contact.blocked}
              onClick={() => start(contact.phone, contact.name)}
              className="flex w-full items-center gap-3 border-b border-gray-100 p-3 text-left hover:bg-gray-50 disabled:opacity-40"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-wa-teal text-sm font-semibold text-white">
                {(contact.name || contact.phone).slice(0, 2).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-gray-900">
                  {contact.name || formatPhone(contact.phone)}
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Phone className="h-3 w-3" />
                  {formatPhone(contact.phone)}
                  {contact.blocked && (
                    <span className="ml-1 text-danger-600">· Blocked</span>
                  )}
                  {contact.optIn.status === 'opted_out' && (
                    <span className="ml-1 text-danger-600">· Opted out</span>
                  )}
                </span>
              </span>
            </button>
          ))}

          {matches.length === 0 && !isNewNumber && (
            <p className="p-6 text-center text-xs text-gray-500">
              {term
                ? 'No contacts match. Type a full phone number to start a new chat.'
                : 'No contacts yet. Type a phone number to start one.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewChatDialog;
